/**
 * Phần thuần của màn Cấu hình lỗi (`error-codes.ts` của web-portal native): giá trị ban đầu, kiểm tra cùng luật với
 * máy chủ (`modules/rpa/backend/src/loi/setting-loi.ts`) để báo ngay ở ô nhập, chuẩn hoá trước khi gửi.
 *
 * Hàm trả **mã lỗi**, không trả câu: view tự dịch.
 */
import type { ChiTietLoi, CodeLoi, MucDo, ThanChiTietLoi, ThanCodeLoi } from '../types.ts';

export const THU_LAI = { min: 0, max: 10 } as const;
export const GIAN_CACH_TOI_DA = 3600;
export const MAU_EXCEPTION = /^[A-Za-z_][A-Za-z0-9_.`+]*$/;
export const DAI_EXCEPTION = 300;
/** Trần độ dài của máy chủ (`GIOI_HAN`). */
export const GIOI_HAN = { ten: 120, moTa: 600, thongDiep: 500, phanLoai: 60 } as const;

export const MUC_DO: readonly MucDo[] = ['critical', 'high', 'medium', 'low'];

export type LoiCode = Partial<Record<'name' | 'maxRetries' | 'retryDelaySeconds', 'batBuoc' | 'saiKhoang'>>;
export type LoiChiTiet = Partial<Record<'exception', 'batBuoc' | 'saiDang'>>;

/** Code mới bắt đầu bằng chính sách mặc định của máy chủ: không thử lại, bỏ qua và báo. */
export function codeTrong(): ThanCodeLoi {
  return {
    name: '',
    foreseen: false,
    description: '',
    severity: 'medium',
    maxRetries: 0,
    retryDelaySeconds: 0,
    onExhausted: 'SKIP',
    enabled: true,
  };
}

export function codeThanhForm(code: CodeLoi): ThanCodeLoi {
  const { name, foreseen, description, severity, maxRetries, retryDelaySeconds, onExhausted, enabled } = code;
  return { name, foreseen, description, severity, maxRetries, retryDelaySeconds, onExhausted, enabled };
}

const trongKhoang = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

export function kiemCode(than: ThanCodeLoi): LoiCode {
  const loi: LoiCode = {};
  if (!than.name.trim()) loi.name = 'batBuoc';
  if (!trongKhoang(than.maxRetries, THU_LAI.min, THU_LAI.max)) loi.maxRetries = 'saiKhoang';
  if (!trongKhoang(than.retryDelaySeconds, 0, GIAN_CACH_TOI_DA)) loi.retryDelaySeconds = 'saiKhoang';
  return loi;
}

export function chuanHoaCode(than: ThanCodeLoi): ThanCodeLoi {
  return { ...than, name: than.name.trim(), description: than.description.trim() };
}

export function chiTietTrong(exception = ''): ThanChiTietLoi {
  return { exception, messageContains: '', classification: '', enabled: true };
}

export function chiTietThanhForm(ct: ChiTietLoi): ThanChiTietLoi {
  const { exception, messageContains, classification, enabled } = ct;
  return { exception, messageContains, classification, enabled };
}

export function kiemChiTiet(than: ThanChiTietLoi): LoiChiTiet {
  const ten = than.exception.trim();
  if (!ten) return { exception: 'batBuoc' };
  if (ten.length > DAI_EXCEPTION || !MAU_EXCEPTION.test(ten)) return { exception: 'saiDang' };
  return {};
}

export function chuanHoaChiTiet(than: ThanChiTietLoi): ThanChiTietLoi {
  return {
    ...than,
    exception: than.exception.trim(),
    messageContains: than.messageContains.trim(),
    classification: than.classification.trim(),
  };
}

/** Tên ngắn để đọc nhanh: `System.Net.WebException` → `WebException`. */
export function tenNganException(exception: string): string {
  const phan = exception.split('.');
  return phan[phan.length - 1] || exception;
}

/** Phân loại đã dùng, thêm giá trị đang gõ (đứng đầu) khi chưa có; không phân biệt hoa thường, không trùng. */
export function luaChonPhanLoai(daCo: readonly string[], dangGo: string): { value: string; moi: boolean }[] {
  const muon = dangGo.trim();
  const biet = [...new Set(daCo.map((c) => c.trim()).filter(Boolean))];
  const moi = muon !== '' && !biet.some((c) => c.toLowerCase() === muon.toLowerCase());
  return [...(moi ? [{ value: muon, moi: true }] : []), ...biet.map((value) => ({ value, moi: false }))];
}

/** Code đang xem: code đã chọn nếu còn trong danh sách, không thì code đầu tiên. */
export function codeDangXem(codes: readonly CodeLoi[], daChon: string): CodeLoi | null {
  return codes.find((c) => c.code === daChon) ?? codes[0] ?? null;
}
